/* eslint-disable react/jsx-props-no-spreading */
import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { styled } from "@iventis/styles";
import { ICellEditor } from "@ag-grid-community/core";
import { TimeWithPrepositionSelector } from "@iventis/components";
import { ZERO_TIME_SPAN } from "@iventis/utilities";
import { fontSizes } from "@iventis/styles/src/atomic-rules";
import { Content } from "@iventis/translations/content/typed-content";
import { Popover } from "@mui/material";
import { IventisTableData, RowNetworkStatus, IventisCellEditorParams } from "../types/data-table.types";
import { cellEditorBase } from "../styles/data-table.styles";

/**
 * Used as cellEditor for data table column of type "timeSpan"
 */
export const TimeWithPrepositionCellEditor = forwardRef<ICellEditor, IventisCellEditorParams>(({ validator, node, required, eGridCell, ...props }, ref) => {
    const translate = props.context?.translate;
    const [newTimeSpanValue, setNewTimeSpanValue] = useState(props.value || ZERO_TIME_SPAN);
    const [timeSpanError, setTimeSpanError] = useState(null);

    useEffect(() => {
        setTimeSpanError(validator(node, props.colDef.field as keyof IventisTableData, newTimeSpanValue));
    }, [newTimeSpanValue]);

    useImperativeHandle(ref, () => ({
        // The final value to send back to the grid once finished editing
        getValue() {
            return newTimeSpanValue;
        },
        // Gives the editor a chance to cancel the edit before the edit has even started by the user.
        isCancelBeforeStart() {
            // If the field is not required or has a value, while we are pending creation, make this cell uneditable
            const blockEditsUntilCreatedOnServer = (!required || props.value != null) && node.data.networkStatus === RowNetworkStatus.PendingCreation;
            return !node.data.canEdit || blockEditsUntilCreatedOnServer;
        },
        // Gives the editor a chance to cancel the edit after the edit has been made. Good for validation.
        isCancelAfterEnd() {
            return timeSpanError?.doesFail || false;
        },
        isPopup() {
            return true;
        },
    }));

    return (
        <Popover
            open
            anchorEl={eGridCell}
            onClose={() => props.api.stopEditing(true)}
            anchorOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            transformOrigin={{
                vertical: "top",
                horizontal: "left",
            }}
        >
            <StyledWrapper>
                <TimeWithPrepositionSelector
                    timeSpanValue={newTimeSpanValue}
                    onChange={(e: string) => setNewTimeSpanValue(e)}
                    name="zeroTime"
                    afterPrepositionLabel={translate(Content.timerules.preposition_lables.after, { type: node.data?.type?.name.toLowerCase() }) || "after start"}
                    beforePrepositionLabel={translate(Content.timerules.preposition_lables.before, { type: node.data?.type?.name.toLowerCase() }) || "before start"}
                />
                {timeSpanError && timeSpanError.doesFail && (
                    <div className="helper-text">
                        <p>{timeSpanError.doesFail ? <span>{translate(timeSpanError.errorMessage)}</span> : ""}</p>
                    </div>
                )}
            </StyledWrapper>
        </Popover>
    );
});

const StyledWrapper = styled.div`
    display: grid;
    position: relative;
    grid-template-columns: auto auto auto;
    grid-template-rows: auto auto;
    gap: 5px;
    align-items: center;
    padding: 10px;
    ${cellEditorBase}

    .helper-text {
        grid-column: 1 /4;
        grid-row: 2 / 3;
        color: red;
        font-size: ${fontSizes.xSmall};
        display: flex;
        flex-direction: column;
        text-align: end;

        p {
            margin-top: 5px;
        }

        p:first {
            margin-top: 0px;
        }
    }
`;
